<template>
    <div v-if="worklogData">
        <form-group :errors="inputErrors['logged_time']">
            <label slot="label" for="logged_time" class="col-sm-2 col-form-label required">{{ trans('tasks.work_logs.time') }}</label>
            <div slot="field" class="worklog-datepicker">
                <div class="input-group">
                    <span class="input-group-addon"><icon :icon="'far fa-clock'"></icon></span>
                    <input type="text"
                           id="logged_time"
                           class="form-control"
                           @change="loggedTime = worklogData.logged_time"
                           v-model="worklogData.logged_time"
                           :disabled="editTimesDisabled">
                </div>
                <div class="help-block"><small>{{ trans('common.for_example_short') }} 3h 45m</small></div>
            </div>
        </form-group>

        <form-group :errors="inputErrors['work_remaining']">
            <label slot="label" for="work_remaining" class="col-sm-2 col-form-label">{{ trans('tasks.attributes.work_remaining') }}</label>
            <div slot="field" class="worklog-datepicker">
                <div class="input-group">
                    <span class="input-group-addon"><icon :icon="'far fa-clock'"></icon></span>
                    <input  type="text"
                            id="work_remaining"
                            class="form-control"
                            v-model="worklogData.work_remaining"
                            @change="remainingTime = worklogData.work_remaining"
                    >
                </div>
                <div class="help-block"><small>{{ trans('common.for_example_short') }} 3h 45m</small></div>
            </div>
        </form-group>

        <form-group :errors="inputErrors['logged_at']">
            <label slot="label" :for="'logged_at-input'" class="col-sm-2 col-form-label required">{{ trans('tasks.work_logs.date') }}</label>
            <div slot="field" class="input-group">
                <datepicker id="logged_at"
                            :style-class="'worklog-datepicker'"
                            :selected="worklogData.logged_at_date"
                            @update="worklogData.logged_at_date = $event"></datepicker>
                <timepicker class="pt-2 pt-lg-0 ml-lg-2"
                            :selected="worklogData.logged_at_time"
                            :style-class="'timepicker-width-lg worklog-datepicker'"
                            @update="worklogData.logged_at_time = $event"></timepicker>
            </div>
        </form-group>

        <form-group :errors="inputErrors['comment']">
            <label slot="label" for="comment" class="col-sm-2 col-form-label">{{ trans('tasks.comments.comment') }}</label>
            <textarea slot="field" id="comment" v-model="worklogData.comment" class="form-control" rows="8"></textarea>
        </form-group>

        <!-- Only available for "partially charged" configured projects -->
        <div v-if="allowPartialCharge">
            <form-group :errors="inputErrors['is_partial_charge']" class="mt-3">
                <label slot="label" class="col-sm-2 col-form-label"></label>
                <div class="checkbox col-form-label" slot="field">
                    <label>
                        <input type="checkbox" v-model="worklogData.is_partial_charge" :disabled="editTimesDisabled">
                        <span class="pl-1">{{ trans('tasks.work_logs.suggest_partial_charge') }}</span>
                    </label>
                </div>
            </form-group>

            <form-group :errors="inputErrors['partial_charge_value']" v-if="worklogData.is_partial_charge">
                <label slot="label" for="partial_charge_value" class="col-sm-2 col-form-label required">{{ trans('tasks.work_logs.suggestion') }}</label>
                <div slot="field" class="worklog-datepicker">
                    <div class="input-group">
                        <span class="input-group-addon"><icon :icon="'far fa-clock'"></icon></span>
                        <input type="text"
                               id="partial_charge_value"
                               class="form-control"
                               v-model="worklogData.partial_charge_value"
                               :disabled="editTimesDisabled">
                    </div>
                </div>
            </form-group>

            <form-group :errors="inputErrors['partial_charge_comment']" v-if="worklogData.is_partial_charge">
                <label slot="label" for="partial_charge_comment" class="col-sm-2 col-form-label required">{{ trans('tasks.comments.comment') }}</label>
                <textarea slot="field"
                          id="partial_charge_comment"
                          maxlength="191"
                          v-model="worklogData.partial_charge_comment"
                          class="form-control"
                          rows="8"></textarea>
            </form-group>
        </div>
    </div>
</template>

<script>

    import FormGroup from '@/components/Forms/FormGroupHorizontal';
    import Icon from '@/components/Icons/Icon';
    import momentTz from 'moment-timezone';
    import Datepicker from '@/components/DateTimepicker/Datepicker';
    import Timepicker from '@/components/DateTimepicker/Timepicker';
    import Alerts from "../../../../components/Alerts/Alerts";

    import WorkLogController from '@/services/Tasks/TaskWorkLogController';

    export default {

        props: {
            inputErrors: {
                type: Object,
                default: () => {
                    return {};
                },
            },
            worklog: {
                type: Object,
                default: null,
            },
            remainingWork: {
                type: String,
                default: '',
            },
            loggedTimePreset: {
                type: String,
                default: '',
            },
            allowPartialCharge: {
                type: Number,
                default: false,
            }
        },

        components: {Alerts, FormGroup, Icon, Datepicker, Timepicker},

        data() {
            return {
                worklogData: null,
                loggedTime: this.worklog ? this.worklog.logged_time_human_readable : 0,
                remainingTime: 0,
                previousSuccessfulLoggedTime: null,
                usePreviousSuccessfulLoggedTime: false,
                editTimesDisabled: this.worklog && this.worklog.has_invoice_postions === true
            };
        },

        mounted() {
            if (this.worklog && this.worklog.exists) {
                // Make a copy of the original data to avoid live changes in UI
                this.worklogData = Object.assign(Object.assign({}, this.worklog), {
                    logged_time: this.worklog.logged_time_human_readable,
                    work_remaining: this.remainingWork,
                    logged_at: this.userFormattedDatetime(this.worklog.logged_at, 'YYYY-MM-DD HH:mm'),
                    partial_charge_value: this.worklog.partial_charge_value_human_readable
                });
            } else {
                this.worklogData = {
                    logged_time: this.loggedTimePreset,
                    work_remaining: this.remainingWork,
                    logged_at: this.currentDatetimeFormatted('YYYY-MM-DD HH:mm'),
                    comment: '',
                    is_partial_charge: false,
                    partial_charge_value: '',
                    partial_charge_comment: ''
                };
            }

            // split logged_at into data and time
            this.worklogData.logged_at_date = this.userFormattedDatetime(this.worklogData.logged_at, 'YYYY-MM-DD');
            this.worklogData.logged_at_time = {
                HH: momentTz(this.worklogData.logged_at).format('HH'),
                mm: (Math.floor(momentTz(this.worklogData.logged_at).format('mm') / 5) * 5).toString().padStart(2, '0')
            };

            // trigger update of time remaining
            if (this.loggedTimePreset) {
                this.loggedTime = this.worklogData.logged_time;
            }
        },

        methods: {
            getModel() {
                this.worklogData.logged_at = this.combineLoggedAt();

                const worklog = Object.assign({}, this.worklogData);

                return Object.assign(worklog, {
                    logged_at: this.toServerDatetimeFormat(worklog.logged_at, 'YYYY-MM-DD HH:mm')
                });
            },

            combineLoggedAt() {
                const time = this.worklogData.logged_at_time;
                const date = momentTz(this.worklogData.logged_at_date);

                const loggedAt = `${date.format('YYYY-MM-DD')} ${time.HH}:${time.mm}`;

                return this.toServerDatetimeFormat(loggedAt, 'YYYY-MM-DD HH:mm');
            }
        },

        watch: {
            loggedTime: function (loggedTime, previouslyLoggedTime) {
                if (this.worklogData.logged_time === 0 || this.worklogData.logged_time === '0') {
                    this.worklogData.logged_time = '0d';
                }
                if (this.worklogData.work_remaining === 0 || this.worklogData.work_remaining === '0') {
                    this.worklogData.work_remaining = '0d';
                }

                const params = {
                    logged_time: this.worklogData.logged_time,
                    remaining_work: this.worklogData.work_remaining,
                    previously_logged_time: this.usePreviousSuccessfulLoggedTime
                        ? this.previousSuccessfulLoggedTime
                        : previouslyLoggedTime
                };

                WorkLogController.changeRemainingWork(params).then(response => {
                    this.worklogData.work_remaining = response.data;

                    this.previousSuccessfulLoggedTime = params.logged_time;
                    this.usePreviousSuccessfulLoggedTime = false;
                }).catch(() => {
                    this.usePreviousSuccessfulLoggedTime = true;
                });
            },

            remainingTime: function () {
                const params = {
                    remaining_work: this.worklogData.work_remaining,
                    previously_remaining_time: this.remainingWork,
                };

                this.$emit('isLoading', true);

                WorkLogController.isRescheduleRequired(params).then(response => {
                    this.$emit('needToReschedule', response.data);
                    this.$emit('isLoading', false);
                });
            },
        }
    };
</script>



// WEBPACK FOOTER //
// src/views/Tasks/WorkLog/Partials/Form.vue